import { createContext, useContext } from 'react';
import type { ThemeMode } from './ptcThemes';

/**
 * Theme-mode context + resolution helpers, split out of ThemeContext.tsx so
 * that file only exports React components (react-refresh convention — see also
 * utils/dueRange.ts, components/state-tax/obligationSubmitMode.ts).
 *
 * Resolution: OS `prefers-color-scheme` is the default; an explicit user choice
 * (the header sun/moon toggle) is persisted to localStorage under
 * `statetax:theme` and overrides the OS from then on. While no explicit choice
 * is stored, the app live-follows OS theme changes.
 */
export const THEME_STORAGE_KEY = 'statetax:theme';

export function readStoredMode(): ThemeMode | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null; // storage unavailable (private mode / blocked) — fall back to OS
  }
}

export function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Exported for tests: stored override wins; otherwise the OS preference. */
export function resolveInitialMode(): ThemeMode {
  return readStoredMode() ?? (systemPrefersDark() ? 'dark' : 'light');
}

export interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined,
);

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return ctx;
}
